import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../../Layout/DashboardLayout";
import ConfigTable from "../../../Common/Tables/ConfigTable";
import {
  Button,
  PageHeading,
  Icon,
  ConfirmDialog,
  Dropdown,
  employeeID,
  companyID,
  sanitizeLabName,
  normalizeSpaces,
} from "../../../Common/UIComponents";
import {
  fetchHolidays,
  fetchHolidayTypes,
  saveHoliday,
  deleteTwoColumnListEntry,
} from "../../../API_Service/Settings_Slice";

const HOLIDAY_NAME_MAX = 50;

const CharCounter = ({ value, max }) => {
  const len = (value ?? "").length;
  if (len === 0) return null;
  const atLimit = len === max;
  return (
    <div className={`flex items-center gap-1 mt-1 text-xs ${atLimit ? "text-red-500 font-semibold" : "text-[#3e494a]/60"}`}>
      <span>{len}/{max}</span>
      {atLimit && <span className="text-red-500">— limit reached</span>}
    </div>
  );
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const toTypeOptions = (items = []) =>
  items.map((item) => ({
    label: item.HolidayTypeName ?? item.Holiday_Type_Name ?? item.Name  ?? item.label ?? String(item),
    value: String(item.HolidayTypeID ?? item.Holiday_Type_Id ?? item.ID ?? item.value ?? ""),
  }));

// ── Date parsing ──────────────────────────────
const parseDate = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date && !isNaN(raw)) return raw;
  const s = String(raw).trim();
  const direct = new Date(s);
  if (!isNaN(direct)) return direct;
  const m = s.match(/^(\d{2})-(\d{2})-(\d{4})\s+(.+)$/);
  if (m) {
    const reordered = new Date(`${m[2]}/${m[1]}/${m[3]} ${m[4]}`);
    if (!isNaN(reordered)) return reordered;
  }
  return null;
};

const fmtDisplay = (raw) => {
  const d = parseDate(raw);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};
const fmtMonth = (raw) => {
  const d = parseDate(raw);
  if (!d) return "";
  return d.toLocaleString("en-US", { month: "short" }).toUpperCase();
};
const fmtDay = (raw) => {
  const d = parseDate(raw);
  if (!d) return "";
  return d.getDate().toString().padStart(2, "0");
};
const fmtInputDate = (raw) => {
  const d = parseDate(raw);
  if (!d) return "";
  const y   = d.getFullYear();
  const mo  = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
};

const buildTableRows = (items = []) =>
  items.map((item, i) => ({
    id:          item.HolidayID       ?? `row-${i}`,
    name:        item.HolidayName     ?? "—",
    typeId:      String(item.HolidayTypeID  ?? ""),
    typeName:    item.HolidayTypeName ?? "—",
    fromDate:    item.HolidayFromDate ?? "",
    toDate:      item.HolidayToDate   ?? "",
    fromDisplay: fmtDisplay(item.HolidayFromDate ?? ""),
    toDisplay:   fmtDisplay(item.HolidayToDate   ?? ""),
    month:       fmtMonth(item.HolidayFromDate   ?? ""),
    day:         fmtDay(item.HolidayFromDate     ?? ""),
    toMonth:     fmtMonth(item.HolidayToDate     ?? ""),
    toDay:       fmtDay(item.HolidayToDate       ?? ""),
  }));

const EXPORT_COLUMNS = [
   { key: "name",        title: "Holiday Name" },
  { key: "fromDisplay", title: "From Date"    },
  { key: "toDisplay",   title: "To Date"      },
  { key: "typeName",    title: "Holiday Type" },
];

const EMPTY_FORM = { holidayTypeId: "", name: "", fromDate: "", toDate: "" };

const rangeOverlapsHoliday = (fromStr, toStr, holidays, excludeId = null) =>
  holidays.some((h) => {
    if (excludeId && h.id === excludeId) return false;
    const hFrom = fmtInputDate(h.fromDate);
    const hTo   = h.toDate ? fmtInputDate(h.toDate) : hFrom;
    if (!hFrom) return false;
    const effectiveTo = toStr || fromStr;
    return fromStr <= hTo && hFrom <= effectiveTo;
  });

// ─────────────────────────────────────────────
// DATE BADGE
// ─────────────────────────────────────────────

const DateBadge = ({ month, day }) => {
  if (!month && !day)
    return (
      <div className="w-10 h-10 rounded-lg bg-[#e5e9e9] flex items-center justify-center text-[#08757c] flex-shrink-0">
        <span className="material-symbols-outlined text-[18px]">event</span>
      </div>
    );
  return (
    <div className="w-10 h-10 rounded-lg bg-[#e5e9e9] flex flex-col items-center justify-center text-[#08757c] leading-tight flex-shrink-0">
      <span className="text-[9px] font-black uppercase">{month}</span>
      <span className="text-sm font-extrabold leading-none">{day}</span>
    </div>
  );
};

// ─────────────────────────────────────────────
// FIELD LABEL
// ─────────────────────────────────────────────

const FieldLabel = ({ children, required }) => (
  <label className="block text-xs font-bold text-[#3e494a] uppercase tracking-wider mb-2">
    {children}
    {required && <span className="text-[#ba1a1a] ml-1">*</span>}
  </label>
);

// ─────────────────────────────────────────────
// DATE INPUT
// ─────────────────────────────────────────────

const DateInput = ({ value, onChange, error, min }) => {
  const ref = useRef(null);
  return (
    <div>
      <div className="cursor-pointer" onClick={() => ref.current?.showPicker()}>
        <input
          ref={ref}
          type="date"
          value={value}
          onChange={onChange}
          min={min}
          onKeyDown={(e) => e.preventDefault()}
          className={`w-full bg-[#f1f4f4] border-none rounded-lg px-4 py-3 text-[#181c1d]
                      font-medium focus:ring-2 focus:ring-[#08757c]/20 outline-none
                      transition-all appearance-none cursor-pointer
                      ${error ? "ring-2 ring-[#ba1a1a]" : ""}`}
        />
      </div>
      {error && <p className="text-xs text-[#ba1a1a] mt-1">{error}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────
// HOLIDAY DRAWER
// ─────────────────────────────────────────────

const HolidayDrawer = ({
  isOpen, onClose, onSave, isSaving, saveError, setSaveError, saveSuccess,
  editRow, holidayTypeOptions, loadingTypes, existingHolidays = [],
}) => {
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editRow) {
      setForm({
        holidayTypeId: editRow.typeId  ?? "",
        name:          editRow.name    ?? "",
        fromDate:      fmtInputDate(editRow.fromDate),
        toDate:        fmtInputDate(editRow.toDate),
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
  }, [editRow, isOpen]);

  // ── Clear form after successful add so user can enter another ────────────
  useEffect(() => {
    if (saveSuccess && !editRow) {
      setForm(EMPTY_FORM);
      setErrors({});
    }
  }, [saveSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

const handleTextField = (field) => (e) => {
  const value = field === "name" ? sanitizeLabName(e.target.value) : e.target.value;
  const excludeId = editRow?.id ?? null;

  setForm((prev) => {
    if (field === "fromDate") return { ...prev, fromDate: value, toDate: "" };
    return { ...prev, [field]: value };
  });

  if (field === "fromDate" && value) {
    const overlaps = rangeOverlapsHoliday(value, value, existingHolidays, excludeId);
    setErrors((prev) => ({
      ...prev,
      fromDate: overlaps ? "This date is already declared as a holiday." : "",
      toDate: "",
    }));
  } else if (field === "toDate" && value) {
    setForm((prev) => {
      const fromDate = prev.fromDate;
      if (fromDate) {
        const overlaps = rangeOverlapsHoliday(fromDate, value, existingHolidays, excludeId);
        setErrors((errs) => ({
          ...errs,
          toDate: overlaps
            ? "The selected date range overlaps with an existing holiday."
            : value < fromDate
            ? "To Date cannot be before From Date."
            : "",
        }));
      }
      return prev;
    });
  } else {
    setErrors((prev) => ({ ...prev, [field]: "" }));
    if (field === "name") setSaveError(null);
  }
};

  const handleTypeSelect = (val) => {
    setForm((prev) => ({ ...prev, holidayTypeId: val }));
    setErrors((prev) => ({ ...prev, holidayTypeId: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.holidayTypeId) errs.holidayTypeId = "Holiday Type is required.";
    if (!form.name.trim())   errs.name          = "Holiday Name is required.";
    if (!form.fromDate) {
      errs.fromDate = "From Date is required.";
    } else if (rangeOverlapsHoliday(form.fromDate, form.toDate || form.fromDate, existingHolidays, editRow?.id ?? null)) {
      const msg = "The selected date range overlaps with an existing holiday.";
      errs.fromDate = msg;
      if (form.toDate) errs.toDate = msg;
    }
    if (!errs.toDate && form.toDate && form.fromDate && form.toDate < form.fromDate)
      errs.toDate = "To Date cannot be before From Date.";
    return errs;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSave({ ...form, name: form.name.trim() }, editRow?.id ?? null);
  };

  const dateRangeLabel = useMemo(() => {
    if (!form.fromDate) return null;
    const from = new Date(form.fromDate).toLocaleDateString("en-US", {
      weekday: "short", month: "long", day: "numeric", year: "numeric",
    });
    if (!form.toDate) return from;
    const to = new Date(form.toDate).toLocaleDateString("en-US", {
      weekday: "short", month: "long", day: "numeric", year: "numeric",
    });
    return `${from} — ${to}`;
  }, [form.fromDate, form.toDate]);

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-all duration-300 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        onClick={onClose}
      />
      <aside
        className={`fixed right-0 top-0 z-[60] h-screen w-full max-w-md bg-white shadow-2xl
                    flex flex-col transition-transform duration-300 ease-in-out
                    ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="p-4 border-b border-[#f1f4f4] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-[#08757c]">calendar_add_on</span>
            <h3 className="text-xl font-bold text-[#181c1d]">
              {editRow ? "Edit Holiday" : "Configure New Holiday"}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#f1f4f4] rounded-full transition-colors">
            <span className="material-symbols-outlined text-[#3e494a]">close</span>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 p-5 space-y-4 overflow-y-auto">

          {/* Error banner */}
          {saveError && (
            <div className="bg-red-50 text-[#ba1a1a] rounded-xl px-4 py-3 text-sm flex items-center gap-3">
              <span className="material-symbols-outlined text-[20px]">error</span>
              {saveError}
            </div>
          )}

          {/* Success banner — create mode only, shown inside drawer */}
          {saveSuccess && !editRow && (
            <div className="bg-[#bbf46e]/20 text-[#436900] rounded-xl px-4 py-3 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
              <span className="material-symbols-outlined text-[20px] text-[#436900]">check_circle</span>
              <span className="font-semibold">Holiday created successfully. You can add another one.</span>
            </div>
          )}

          {/* 1. Holiday Type */}
          <div>
            <FieldLabel required>Holiday Type</FieldLabel>
            {loadingTypes ? (
              <div className="flex items-center gap-2 text-sm text-[#3e494a] py-3">
                <span className="material-symbols-outlined animate-spin text-[#08757c] text-[18px]">progress_activity</span>
                Loading types…
              </div>
            ) : (
              <Dropdown
                options={holidayTypeOptions}
                value={form.holidayTypeId}
                onChange={handleTypeSelect}
                placeholder="Select Holiday Type"
                searchPlaceholder="Search type…"
                error={errors.holidayTypeId}
              />
            )}
          </div>

          {/* 2. Holiday Name */}
          <div>
            <FieldLabel required>Holiday Name</FieldLabel>
            <input
              type="text"
              value={form.name}
              onChange={handleTextField("name")}
              placeholder="e.g. New Year's Day"
              maxLength={HOLIDAY_NAME_MAX}
              className={`w-full bg-[#f1f4f4] border-none rounded-lg px-4 py-3 text-[#181c1d]
                          font-medium focus:ring-2 focus:ring-[#08757c]/20 outline-none transition-all
                          ${errors.name ? "ring-2 ring-[#ba1a1a]" : ""}`}
            />
            {errors.name && <p className="text-xs text-[#ba1a1a] mt-1">{errors.name}</p>}
            <CharCounter value={form.name} max={HOLIDAY_NAME_MAX} />
          </div>

          {/* 3. From Date + To Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel required>From Date</FieldLabel>
              <DateInput value={form.fromDate} onChange={handleTextField("fromDate")} error={errors.fromDate} />
            </div>
            <div>
              <FieldLabel>To Date</FieldLabel>
              <DateInput value={form.toDate} onChange={handleTextField("toDate")} error={errors.toDate} min={form.fromDate} />
            </div>
          </div>

          {/* Live preview */}
          {form.fromDate && (
            <div className="flex items-center gap-3 p-3 bg-[#f1f4f4] rounded-xl">
              <DateBadge month={fmtMonth(form.fromDate)} day={fmtDay(form.fromDate)} />
              <div className="min-w-0">
                <p className="text-[10px] text-[#3e494a]/60 font-bold uppercase tracking-widest">
                  {form.toDate ? "Date Range" : "From Date"}
                </p>
                <p className="text-sm font-semibold text-[#181c1d] truncate">{dateRangeLabel}</p>
              </div>
            </div>
          )}

          <div className="bg-[#f1f4f4]/50 p-4 rounded-lg">
            <p className="text-xs text-[#3e494a] italic leading-relaxed">
              <span className="text-[#ba1a1a] font-bold">*</span> Holiday Type, Name and From Date
              are mandatory. To Date is optional — leave blank for single-day holidays.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-[#f1f4f4] flex gap-3 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 px-6 py-3 rounded-lg text-[#3e494a] font-bold text-sm hover:bg-[#e5e9e9] transition-colors">
            Cancel
          </button>
          <button type="submit" onClick={handleSubmit} disabled={isSaving}
            className="flex-[2] px-8 py-3 bg-[#08757c] text-white rounded-lg font-bold text-sm
                       shadow-lg shadow-[#08757c]/20 hover:opacity-90 active:scale-[0.98]
                       transition-all disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center gap-2">
            {isSaving ? (
              <><span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>Saving…</>
            ) : (
              editRow ? "Update Holiday" : "Save Holiday"
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

// ─────────────────────────────────────────────
// STATS ROW
// ─────────────────────────────────────────────

const StatsRow = ({ rows }) => {
  const total    = rows.length;
  const upcoming = useMemo(
    () => rows.filter((r) => { const d = parseDate(r.fromDate); return d && d >= new Date(); }).length,
    [rows]
  );
  const thisYear = useMemo(
    () => rows.filter((r) => { const d = parseDate(r.fromDate); return d && d.getFullYear() === new Date().getFullYear(); }).length,
    [rows]
  );

  const stats = [
    { label: "Total Holidays", value: total,    icon: "event",          bg: "bg-[#005b60]/10", color: "text-[#005b60]" },
    { label: "This Year",      value: thisYear, icon: "calendar_today", bg: "bg-[#8fc444]/10", color: "text-[#436900]" },
    { label: "Upcoming",       value: upcoming, icon: "event_upcoming", bg: "bg-[#0088cc]/10", color: "text-[#0088cc]" },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
      {stats.map((s) => (
        <div key={s.label} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center ${s.color} flex-shrink-0`}>
            <span className="material-symbols-outlined text-[22px]">{s.icon}</span>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">{s.label}</p>
            <p className="text-xl font-bold text-slate-900 leading-tight">{total === 0 ? "—" : s.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function HolidayCalendarIndex({ embedded = false } = {}) {
  const dispatch = useDispatch();
  const location = useLocation();

  const [holidays,           setHolidays]           = useState([]);
  const [holidayTypeOptions, setHolidayTypeOptions] = useState([]);
  const [loadingTypes,       setLoadingTypes]       = useState(false);
  const [isLoading,          setIsLoading]          = useState(false);
  const [settingsError,      setSettingsError]      = useState(null);
  const [deleteLoading,      setDeleteLoading]      = useState(false);
  const [deleteError,        setDeleteError]        = useState(null);
  const [deleteSuccess,      setDeleteSuccess]      = useState(false);
  const [drawerSaveSuccess,  setDrawerSaveSuccess]  = useState(false); // shown inside drawer (add mode)
  const [editSuccess,        setEditSuccess]        = useState(false); // shown as toast (edit mode)
  const [isSaving,           setIsSaving]           = useState(false);
  const [saveError,          setSaveError]          = useState(null);
  const [searchQuery,        setSearchQuery]        = useState("");
  const [currentPage,        setCurrentPage]        = useState(1);
  const [pageSize,           setPageSize]           = useState(10);
  const [deleteDialog,       setDeleteDialog]       = useState({ open: false, row: null });
  const [drawerOpen,         setDrawerOpen]         = useState(false);
  const [editRow,            setEditRow]            = useState(null);
  const [bookingConflict,    setBookingConflict]    = useState({ show: false, form: null, id: null, bookings: [] });

  // ── Fetch ─────────────────────────────────────
  const fetchList = useCallback(() => {
    setIsLoading(true);
    setSettingsError(null);
    dispatch(fetchHolidays({ ID: 0, companyID }))
      .then((res) => setHolidays(toArray(res)))
      .catch((err) => setSettingsError(err?.message ?? "Failed to load holidays"))
      .finally(() => setIsLoading(false));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchList(); }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHolidayTypes = useCallback(() => {
    setLoadingTypes(true);
    dispatch(fetchHolidayTypes({ ID: 0, companyID }))
      .then((res) => setHolidayTypeOptions(toTypeOptions(toArray(res))))
      .catch(() => setHolidayTypeOptions([]))
      .finally(() => setLoadingTypes(false));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Drawer handlers ───────────────────────────
  const openAddDrawer = useCallback(() => {
    setEditRow(null);
    setSaveError(null);
    setDrawerSaveSuccess(false);
    setDrawerOpen(true);
    loadHolidayTypes();
  }, [loadHolidayTypes]);

  const openEditDrawer = useCallback((row) => {
    setEditRow(row);
    setSaveError(null);
    setDrawerSaveSuccess(false);
    setDrawerOpen(true);
    loadHolidayTypes();
  }, [loadHolidayTypes]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setEditRow(null);
    setSaveError(null);
    setDrawerSaveSuccess(false);
  }, []);

  // ── Rows / filter / paginate ──────────────────
  const rows = useMemo(() => buildTableRows(holidays), [holidays]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.fromDisplay.toLowerCase().includes(q) ||
        r.toDisplay.toLowerCase().includes(q) ||
        r.typeName.toLowerCase().includes(q)
    );
  }, [searchQuery, rows]);

  const totalPages    = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  // ── Columns ───────────────────────────────────
  const columns = useMemo(() => [
    {
      key:   "name",
      title: "Holiday Name",
      render: (row) => (
        <span className="font-semibold text-[#181c1d] line-clamp-2 break-words">
          {row.name}
        </span>
      ),
    },
    {
      key:   "fromDate",
      title: "From Date",
      render: (row) => (
        <div className="flex items-center gap-3">
          <DateBadge month={row.month} day={row.day} />
          <span className="font-medium text-[#181c1d] text-sm">{row.fromDisplay}</span>
        </div>
      ),
    },
    {
      key:   "toDate",
      title: "To Date",
      render: (row) =>
        row.toDisplay && row.toDisplay !== "—" ? (
          <div className="flex items-center gap-3">
            <DateBadge month={row.toMonth} day={row.toDay} />
            <span className="font-medium text-[#181c1d] text-sm">{row.toDisplay}</span>
          </div>
        ) : (
          <span className="text-xs text-[#3e494a]/40 italic">Single day</span>
        ),
    },
    
    {
      key:   "typeName",
      title: "Holiday Type",
      render: (row) => (
        <span
          title={row.typeName}
          className="inline-block px-2 py-1 bg-[#e5e9e9] text-[#3e494a] text-xs font-bold rounded-lg line-clamp-2 break-words">
          {row.typeName}
        </span>
      ),
    },
  ], []);

  // ── Save ──────────────────────────────────────
  const handleSave = useCallback((form, id, skipConflictCheck = false) => {
    setIsSaving(true);
    setSaveError(null);
    setDrawerSaveSuccess(false);

    const isEdit = !!id;

    const selectedTypeName =
      holidayTypeOptions.find((o) => String(o.value) === String(form.holidayTypeId))?.label ?? "";

    const payload = {
      Holiday: {
        HolidayID:   id ?? 0,
        HolidayName: normalizeSpaces(form.name),
        FromDate:    form.fromDate,
        ToDate:      form.toDate || null,
      },
      HolidayType: {
        HolidayTypeID:   Number(form.holidayTypeId),
        HolidayTypeName: selectedTypeName,
      },
      CreatedBy: employeeID,
      CompanyID: companyID,
      SkipBookingConflictCheck: skipConflictCheck ? 1 : 0,
    };

    dispatch(saveHoliday(payload))
      .then((res) => {
        const result = res?.payload?.data ?? res?.payload;

        // ✅ Handle new response format (object with success property)
        if (result && typeof result === 'object' && 'success' in result) {
          if (result.success === true) {
            // Success - save and close
            setBookingConflict({ show: false, form: null, id: null, bookings: [] });
            fetchList();

            if (isEdit) {
              closeDrawer();
              setEditSuccess(true);
              setTimeout(() => setEditSuccess(false), 3000);
            } else {
              setDrawerSaveSuccess(true);
              setTimeout(() => setDrawerSaveSuccess(false), 4000);
            }
            setIsSaving(false);
            return;
          }

          // Error case
          if (result.code === -1) {
            setSaveError("A holiday with this name already exists.");
          } else if (result.code === -2) {
            // Booking conflict with lab names
            const labNames = Array.isArray(result.conflictingLabs) ? result.conflictingLabs : [];
            setBookingConflict({
              show: true,
              form,
              id,
              bookings: labNames,
              message: result.message
            });
          } else {
            setSaveError(result.message || "Failed to save. Please try again.");
          }
          setIsSaving(false);
          return;
        }

        // Fallback: Handle old format
        if (Array.isArray(result) && result.length > 0) {
          setBookingConflict({ show: true, form, id, bookings: result });
          setIsSaving(false);
          return;
        }

        const resultValue = Number(result);
        if (resultValue > 0) {
          setBookingConflict({ show: false, form: null, id: null, bookings: [] });
          fetchList();
          if (isEdit) {
            closeDrawer();
            setEditSuccess(true);
            setTimeout(() => setEditSuccess(false), 3000);
          } else {
            setDrawerSaveSuccess(true);
            setTimeout(() => setDrawerSaveSuccess(false), 4000);
          }
        } else if (resultValue === -1) {
          setSaveError("A holiday with this name already exists.");
        } else if (resultValue === -2) {
          setBookingConflict({ show: true, form, id, bookings: [] });
        } else {
          setSaveError("Failed to save. Please try again.");
        }
        setIsSaving(false);
      })
      .catch((err) => {
        setSaveError(err?.message ?? "Failed to save holiday");
        setIsSaving(false);
      });
  }, [dispatch, fetchList, closeDrawer, holidayTypeOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmConflict = useCallback(() => {
    if (bookingConflict.form) {
      handleSave(bookingConflict.form, bookingConflict.id, true);
      setBookingConflict({ show: false, form: null, id: null, bookings: [] });
    }
  }, [bookingConflict, handleSave]);

  // ── Delete ────────────────────────────────────
  const handleDelete       = useCallback((row) => setDeleteDialog({ open: true, row }), []);
  const handleCancelDelete = useCallback(() => setDeleteDialog({ open: false, row: null }), []);

  const handleConfirmDelete = useCallback(() => {
    const { row } = deleteDialog;
    if (!row) return;
    setDeleteLoading(true);
    setDeleteError(null);
    dispatch(deleteTwoColumnListEntry({ term: "HOLIDAY", id: row.id, modifiedBy: employeeID, companyID }))
      .then(() => {
        setDeleteDialog({ open: false, row: null });
        setDeleteSuccess(true);
        fetchList();
        setTimeout(() => setDeleteSuccess(false), 3000);
      })
      .catch((err) => setDeleteError(err?.message ?? "Failed to delete"))
      .finally(() => setDeleteLoading(false));
  }, [deleteDialog, dispatch, fetchList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange   = useCallback((e) => { setSearchQuery(e.target.value); setCurrentPage(1); }, []);
  const handlePageSizeChange = useCallback((size) => { setPageSize(size); setCurrentPage(1); }, []);

  const inner = (
    <>
      <div className="px-8 py-5 bg-[#f7fafa] text-[#181c1d] min-h-screen">

       

        <div className="flex flex-wrap justify-between items-end mb-5 gap-4">
          <PageHeading
            title="Holiday Calendar"
            subtitle="Manage and define system-wide holidays for operational scheduling and reporting. These dates will automatically adjust laboratory TAT and shift expectations."
          />
          <Button variant="primary" onClick={openAddDrawer}>
            <Icon name="calendar_add_on" className="mr-1 text-[20px]" />
            Add New Holiday
          </Button>
        </div>

        <StatsRow rows={rows} />

        <ConfigTable
          columns={columns}
          rows={isLoading ? [] : paginatedRows}
          onEdit={openEditDrawer}
          onDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search by holiday name, date or type…"
          allFilteredRows={filteredRows}
          exportColumns={EXPORT_COLUMNS}
          exportFilename="holidays"
          emptyText={isLoading ? "Loading…" : "No holidays found."}
        />

        {settingsError && (
          <div className="mt-6 rounded-xl bg-red-50 text-red-700 p-6 text-sm">{settingsError}</div>
        )}

        <section className="mt-5 flex gap-3 p-4 bg-[#f1f4f4] rounded-xl items-start border border-[#e5e9e9]">
          <span className="material-symbols-outlined text-[#8fc444] text-2xl flex-shrink-0"
            style={{ fontVariationSettings: "'FILL' 1" }}>info</span>
          <div className="space-y-1">
            <p className="font-bold text-sm text-[#181c1d]">Automated Schedule Adjustment</p>
            <p className="text-sm text-[#3e494a] leading-relaxed">
              Once a holiday is saved, the Lab Planning engine will recalculate all pending TAT
              estimates within 5 minutes. Active shifts overlapping these dates will be flagged
              for review in the Shift Mapping module.
            </p>
          </div>
        </section>
      </div>

      {/* Drawer — saveSuccess passed so banner shows inside for add mode */}
      <HolidayDrawer
        isOpen={drawerOpen} onClose={closeDrawer} onSave={handleSave}
        isSaving={isSaving} saveError={saveError} setSaveError={setSaveError} saveSuccess={drawerSaveSuccess}
        editRow={editRow}
        holidayTypeOptions={holidayTypeOptions} loadingTypes={loadingTypes}
        existingHolidays={rows}
      />

      <ConfirmDialog isOpen={deleteDialog.open} variant="danger" isLoading={deleteLoading}
        onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} />

      {/* Edit success toast (edit mode only — fires after drawer closes) */}
      {editSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
          <Icon name="check_circle" className="text-[20px]" filled /> Holiday Calendar Updated successfully.
        </div>
      )}
      {deleteSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
          <Icon name="check_circle" className="text-[20px]" filled /> Holiday Calendar Deleted successfully.
        </div>
      )}
      {deleteError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-red-600 text-white text-sm font-semibold px-6 py-3 rounded-xl shadow-lg">
          {deleteError}
        </div>
      )}

      {/* Booking Conflict Popup */}
      {bookingConflict.show && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <span className="material-symbols-outlined text-[#ba1a1a] text-[24px] flex-shrink-0">warning</span>
              <div className="flex-1">
                <h3 className="font-bold text-[#181c1d] mb-1">Lab Booking Conflict</h3>
                <p className="text-sm text-[#3e494a]">
                  There are existing lab bookings during this holiday period:
                </p>
              </div>
            </div>

            {/* Lab Names List */}
            {bookingConflict.bookings && bookingConflict.bookings.length > 0 && (
              <div className="mb-6 bg-[#e8f5f7] border border-[#b3e5fc] rounded-xl p-4">
                <p className="text-xs font-bold text-[#0277bd] uppercase tracking-wider mb-3">Affected Labs</p>
                <div className="space-y-2">
                  {bookingConflict.bookings.map((labName, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-[#81d4fa]">
                      <span className="material-symbols-outlined text-[#0277bd] text-[20px]">location_on</span>
                      <span className="font-semibold text-[#181c1d]">{labName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-[#fff3cd] border border-[#ffeeba] rounded-lg p-3 mb-6">
              <p className="text-xs text-[#856404]">
                Do you still want to mark this as a holiday? This will not cancel the lab bookings.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setBookingConflict({ show: false, form: null, id: null, bookings: [] })}
                className="flex-1 px-4 py-2 rounded-lg border border-[#e5e9e9] text-[#3e494a] font-semibold hover:bg-[#f7fafa] transition-all"
              >
                No, Cancel
              </button>
              <button
                onClick={handleConfirmConflict}
                disabled={isSaving}
                className="flex-1 px-4 py-2 rounded-lg bg-[#08757c] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <><span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>Saving…</>
                ) : (
                  "Yes, Save Holiday"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
  return embedded ? inner : <DashboardLayout>{inner}</DashboardLayout>;
}

